import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { cn } from "@/cn";
import { listPrompts, type PromptTemplate } from "@/api/prompts";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import {
  categoryColor,
  categoryLabel,
  filterPrompts,
  promptCategoryTabs,
  promptRequirementSummary,
  type PromptCategoryFilter,
} from "@/lib/prompt-ui";

export function SettingsPromptsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<PromptCategoryFilter>("all");

  const { data: prompts = [], isLoading, error } = useQuery<PromptTemplate[]>({
    queryKey: ["prompts"],
    queryFn: () => listPrompts(),
    staleTime: 30_000,
  });

  const categoryTabs = promptCategoryTabs(prompts);

  const filteredPrompts = filterPrompts(prompts, {
    search: searchQuery,
    category: categoryFilter,
  });

  const {
    pageItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(filteredPrompts, [searchQuery, categoryFilter]);

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Prompt Templates</h1>
        <p className="text-muted-foreground">
          System prompt templates used by the AI assistant for different workflows.
        </p>
      </header>

      {!isLoading && !error && prompts.length > 0 && (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, kind, category, or description…"
                className="w-full rounded-md border border-border bg-card py-2 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">
            {filteredPrompts.length === 0
              ? `0 of ${prompts.length}`
              : `Showing ${showingFrom}–${showingTo} of ${filteredPrompts.length}`}
          </span>
        </div>
      )}

      {!isLoading && !error && prompts.length > 0 && categoryTabs.length > 1 && (
        <div className="flex flex-wrap gap-1">
          {categoryTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setCategoryFilter(tab)}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition",
                categoryFilter === tab
                  ? "bg-brand text-brand-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab === "all" ? "All categories" : categoryLabel(tab)}
            </button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">Failed to load prompt templates.</p>}

      {!isLoading && !error && prompts.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No prompt templates found.</p>
        </div>
      )}

      {!isLoading && !error && prompts.length > 0 && filteredPrompts.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">No prompts match your filters.</p>
        </div>
      )}

      {!isLoading && !error && filteredPrompts.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pageItems.map((prompt) => (
              <Link
                key={prompt.kind}
                to={`/settings/sequence/${prompt.kind}`}
                className="flex flex-col rounded-lg border border-border bg-card p-4 transition hover:bg-muted/20"
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <p className="min-w-0 flex-1 text-sm font-medium text-foreground">
                    {prompt.name}
                  </p>
                  {prompt.category && (
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
                        categoryColor(prompt.category),
                      )}
                    >
                      {categoryLabel(prompt.category)}
                    </span>
                  )}
                </div>
                <p className="mb-3 line-clamp-2 min-h-[2.5rem] flex-1 text-xs text-muted-foreground">
                  {prompt.description || prompt.kind}
                </p>
                <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3 text-[10px] text-muted-foreground">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-medium">
                    {prompt.kind}
                  </span>
                  <span>v{prompt.version}</span>
                  <span>&middot;</span>
                  <span>{prompt.parsePattern}</span>
                  <span>&middot;</span>
                  <span>{prompt.maxDuration}s</span>
                </div>
                <p className="mt-2 text-[10px] text-muted-foreground">
                  {promptRequirementSummary(prompt)}
                </p>
              </Link>
            ))}
          </div>
          <PaginationBar
            page={page}
            pageSize={pageSize}
            totalItems={filteredPrompts.length}
            totalPages={totalPages}
            showingFrom={showingFrom}
            showingTo={showingTo}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </div>
  );
}
